import { CALIBRATION_REPS } from '@heraion/shared';

import { emptySet, withEntry } from './loggerDraftHelpers';
import type {
  DraftEntry,
  DraftSet,
  LoggerAction,
  LoggerDraft,
  WeightUnit,
} from './loggerDraftTypes';

// #353: the MAX calibration's two writes, delegated by loggerNestedReducer
// like the group and session clusters (that file sits near the 500 cap).

export type CalibrationAction = Extract<
  LoggerAction,
  { type: 'answer_calibration' | 'append_calibration_set' }
>;

/** A ramp set: 5 reps, the load given (blank for the first, which the
 *  athlete writes), marked as calibration. */
export const calibrationSet = (
  unit: WeightUnit,
  loadedSides: 1 | 2,
  weight: string,
): DraftSet => ({
  ...emptySet(unit, loadedSides),
  reps: String(CALIBRATION_REPS),
  weight,
  calibration: true,
});

/** The entry with the ramp's next set appended; unchanged outside an accepted
 *  calibration. The ONE append: the reducer and the commit's lookahead share it. */
export const appendCalibrationSet = (
  entry: DraftEntry,
  set: DraftSet,
): DraftEntry =>
  entry.calibration === 'accepted'
    ? { ...entry, sets: [...entry.sets, set] }
    : entry;

export function calibrationReducer(
  state: LoggerDraft,
  action: CalibrationAction,
): LoggerDraft {
  if (action.type === 'answer_calibration') {
    return withEntry(state, action.entryIdx, (entry) => {
      if (entry.calibration !== 'offered') return entry;
      if (!action.accept) return { ...entry, calibration: 'declined' };
      // The ramp replaces the prescribed sets, so only before any is logged.
      if (entry.sets.some((s) => s.completedAt !== null)) return entry;
      return {
        ...entry,
        calibration: 'accepted',
        // The effort column is always on in a calibration (14/10).
        effortColumn: true,
        sets: [calibrationSet(action.defaultUnit, entry.loadedSides, '')],
      };
    });
  }
  return withEntry(state, action.entryIdx, (entry) =>
    appendCalibrationSet(entry, action.set),
  );
}
